import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PasswordInput } from "@/knowmeno/PasswordInput";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Set a new password — KnowMeno" },
      { name: "description", content: "Choose a new password for your KnowMeno account and get back to tracking your menopause symptoms in under a minute." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

const RO = "#D4537E";
const GR = "#888780";
const PLUM = "#4A1942";
const WH = "#ffffff";

function ResetPasswordPage() {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState<"pending" | "ok" | "failed">(
    "pending",
  );
  const navigate = useNavigate();

  // The auth hook sends a link with ?token_hash=...&type=recovery&next=...
  // We exchange it for a session. (Fallback: Supabase's default link puts the
  // recovery session in the URL hash and onAuthStateChange picks it up.)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = new URL(window.location.href);
      const tokenHash = url.searchParams.get("token_hash");
      const type = url.searchParams.get("type") || "recovery";

      if (tokenHash) {
        const { error: vErr } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as any,
        });
        if (cancelled) return;
        if (vErr) {
          setError(vErr.message);
          setVerified("failed");
          return;
        }
        setVerified("ok");
        return;
      }

      // Fallback: hash-based recovery session
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) setVerified("ok");
      else {
        setError(
          "This reset link is invalid or has expired. Please request a new one.",
        );
        setVerified("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: pw });
      if (err) throw err;
      navigate({ to: "/app" });
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(165deg, #FBFAF6 0%, #F7EEEA 60%, #F2E1DA 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: '"Cormorant Garamond", Georgia, serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: "100%",
          background: WH,
          borderRadius: 24,
          padding: "36px 32px",
          boxShadow: "0 30px 80px -30px rgba(74, 25, 66, 0.25)",
        }}
      >
        <div
          style={{
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            display: "flex",
            alignItems: "baseline",
            marginBottom: 10,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <span style={{ color: RO }}>Meno</span>
          <span style={{ color: GR, fontWeight: 300, margin: "0 1px" }}>-</span>
          <span style={{ color: PLUM }}>IQ</span>
        </div>
        <h1
          style={{
            fontSize: 34,
            lineHeight: 1.1,
            color: "#2A1228",
            marginBottom: 8,
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          Set a new password
        </h1>

        {verified === "pending" && (
          <p
            style={{
              color: GR,
              fontSize: 14,
              fontFamily: "system-ui, sans-serif",
              marginTop: 20,
            }}
          >
            Verifying your reset link…
          </p>
        )}

        {verified === "failed" && (
          <p
            style={{
              color: RO,
              fontSize: 14,
              fontFamily: "system-ui, sans-serif",
              marginTop: 20,
            }}
          >
            {error || "This link is invalid."}
          </p>
        )}

        {verified === "ok" && (
          <form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginTop: 18,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <PasswordInput
              required
              minLength={6}
              autoComplete="new-password"
              aria-label="New password"
              placeholder="New password (6+ characters)"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              style={inputStyle}
            />
            <PasswordInput
              required
              minLength={6}
              autoComplete="new-password"
              aria-label="Confirm new password"
              placeholder="Confirm new password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              style={inputStyle}
            />
            {error && <div style={{ color: RO, fontSize: 13 }}>{error}</div>}
            <button
              type="submit"
              disabled={submitting}
              style={{
                background: PLUM,
                color: WH,
                border: "none",
                borderRadius: 14,
                padding: 15,
                fontSize: 15,
                fontWeight: 500,
                cursor: submitting ? "default" : "pointer",
                opacity: submitting ? 0.6 : 1,
                marginTop: 6,
              }}
            >
              {submitting ? "Saving…" : "Save new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid #E0DED7",
  borderRadius: 14,
  padding: "14px 16px",
  fontSize: 15,
  fontFamily: "inherit",
  outline: "none",
  background: WH,
};
